'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import type { TokenPurchaseResultCopy } from '@/types/tokens';

/** Count-up duration/steps — mirrors Apple's 0.8s / 24-step ease-out cubic. */
const COUNT_UP_DURATION_MS = 800;
const COUNT_UP_STEPS = 24;

/**
 * In-sheet token-purchase success state: a calm checkmark, the server-driven
 * title/subtitle, and the credited "+N" counting up so the gain is felt.
 * Pixel-parity with Apple's `TokenPaywallSheet.successColumn` and Android's
 * `TokenPurchaseSuccessContent` — 56px teal checkmark (the ONLY colored
 * element), centered cardTitle + cardBody copy, neutral tokenBalance figure
 * next to the walnut glyph.
 *
 * The new TOTAL is deliberately not repeated here: the Tokens page is one tap
 * away and a second number would dilute the "+N" gain.
 *
 * No "Done" button — the sheet's ✕ dismisses and the host auto-closes after a
 * beat (same as both mobile apps).
 */
export function TokenPurchaseSuccess({
  copy,
  tokensCredited,
  visible = true,
}: {
  /** Server-localized copy from the token-package catalog. Rendered verbatim. */
  copy: TokenPurchaseResultCopy | undefined;
  /** Tokens the purchased package credits (server-owned amount). */
  tokensCredited: number;
  /**
   * Whether this state is actually on screen. False while Paddle's own overlay
   * still covers the sheet: the body mounts the moment payment is confirmed
   * (so a missed `checkout.closed` can never strand the buyer back on the
   * offer), but the count-up must not burn through behind the overlay — it
   * starts when the buyer can see it.
   */
  visible?: boolean;
}) {
  const displayed = useCountUp(tokensCredited, visible);

  return (
    <div className="flex w-full flex-col items-center gap-6 px-2 pb-6 pt-8 text-center">
      {/* Brand teal reads as success while keeping the state on-brand. The
          scale-in mirrors Apple's `symbolEffect(.bounce)`. */}
      <CheckCircle2
        className={cn(
          'text-primary h-14 w-14',
          // Same reason as the count-up: don't spend the pop behind the overlay.
          visible && 'animate-success-pop'
        )}
        strokeWidth={1.8}
        aria-hidden="true"
      />

      {copy ? (
        <div className="flex flex-col gap-1">
          <p className="text-card-title text-foreground font-semibold">
            {copy.title}
          </p>
          <p className="text-card-body text-muted-foreground">
            {copy.subtitle}
          </p>
        </div>
      ) : null}

      <div className="flex items-center gap-1.5">
        <span className="text-token-balance text-foreground">
          {`+${displayed}`}
        </span>
        <WalnutIcon className="h-[22px] w-[18px] text-[#C77B4A]" />
      </div>
    </div>
  );
}

/** Eased (ease-out cubic) count-up from 0 to `target`, run once it's on screen. */
function useCountUp(target: number, run: boolean): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!run) {
      return;
    }
    if (target <= 0) {
      setValue(target);
      return;
    }

    let step = 0;
    const timer = setInterval(() => {
      step += 1;
      const t = step / COUNT_UP_STEPS;
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (step >= COUNT_UP_STEPS) {
        clearInterval(timer);
        setValue(target);
      }
    }, COUNT_UP_DURATION_MS / COUNT_UP_STEPS);

    return () => clearInterval(timer);
  }, [target, run]);

  return value;
}
